import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import AnthropometricService from "./AnthropometricService";
import { useSheetCountRegistration } from "../../components/sheetCounts";
import { useTranscriptModalGate } from "../../components/ActiveCallProvider";
import { useCardDraft } from "../common/useCardDraft";
import { CalendarIcon, ChevronUpIcon } from "../labInvestigations/labIcons";
import { PARAMETER_BY_KEY, BLOOD_GROUPS, BLOOD_GROUP_NONE, normaliseBloodGroup } from "./data";
import { ANTH_V2_REFRESH, ANTH_V2_SELECT_PARAM } from "./events";
// LI writes to mapped SLTs (Weight/Height/BMI/Blood-group/Waist/Hip) are
// what Anthro reads from post-Phase 4. Refresh this rail's cached doc
// when the LI section (or any LI writer) fires its own refresh event.
import { LI_V2_REFRESH } from "../labInvestigations/events";
import { RAIL_OPEN, RAIL_CLOSE } from "../railEvents";

/**
 * Anthropometric editor rail — the right column that sits next to the
 * Content table. Accordion of the 5 user-entered parameters (Height,
 * Weight, Blood group, Waist, Hip). BMI + WHR are auto-calc'd server-side
 * and have no card here.
 *
 * Listens for ANTH_V2_SELECT_PARAM events emitted by the Content table so
 * clicking a parameter name over there opens its card here. Emits
 * ANTH_V2_REFRESH after a successful save so both columns refetch.
 *
 * Unit handling: server stores metric (cm, kg). Height accepts either cm
 * (single number) or ft+in (two inputs) — converted to cm on save. Weight
 * accepts kg or lb — converted to kg. Waist/Hip accept cm or in — converted
 * to cm.
 */

// Only the user-entered parameters appear in the editor accordion.
const EDITOR_KEYS = ["height", "weight", "bloodGroup", "waistCircumference", "hipCircumference"];
const EDITOR_META = EDITOR_KEYS.map((k) => PARAMETER_BY_KEY[k]);

const todayISO = () => new Date().toISOString().slice(0, 10);

// ── Unit conversion helpers ────────────────────────────────────────────
const LB_TO_KG = 0.453592;
const KG_TO_LB = 2.20462;
const IN_TO_CM = 2.54;
const CM_TO_IN = 0.393701;

/**
 * Plausible ranges, in the canonical METRIC unit — checked against
 * prepared.numericValue, so they hold whichever unit is on screen.
 *
 * GENEROUS ON PURPOSE, with real buffer at both ends. The job is to reject
 * nonsense (a 20-digit weight, a slipped decimal, a pasted phone number), not to
 * second-guess a clinician, so each bound sits well outside anything a human
 * record actually holds:
 *
 *   weight  0.2 – 500 kg   height  20 – 300 cm
 *
 * THE FLOOR IS 0.2 kg, NOT 0.5. Extremely-low-birth-weight babies are routine
 * NICU territory and 400 g survivors are documented; the smallest recorded
 * anywhere is 212 g. A 0.5 floor also bit at a culturally standard figure on the
 * pounds path — 1.0 lb converts to 0.45 kg, so the editor refused every birth
 * weight under 1 lb 2 oz, and "a one-pound baby" is a category, not a
 * hypothetical. The floor costs nothing to lower: no keystroke slip lands
 * between 0.2 and 0.5 kg, and zero and below are already refused upstream by
 * `prepared`.
 *
 * THE CEILING STAYS 500 kg even though humans have been weighed at 500 (Mumbai,
 * 2017) and above (635 kg recorded). It is inclusive, so that admission weight
 * passes. Raising it would cost more than it buys: a dropped decimal turns W.d
 * into W*10+d, which only exceeds 500 once W reaches 50 — so a 500 ceiling
 * catches the slip for every adult from 50.0 kg up (55.5 -> 555, caught), and a
 * 600 ceiling would let 555 through silently.
 *
 * Height: a very preterm infant is about 30 cm and the tallest man recorded was
 * 272 cm, so 20-300 clears both ends with room.
 *
 * WAIST AND HIP take the same 20-300 cm band. Both are body circumferences on
 * the same scale as height, and the service's own WHO-derived thresholds put an
 * adult's "elevated" waist at 88 cm (female) / 102 cm (male)
 * (api-anthropometric-v2-service/constants/constants.go:96-113), so 300 leaves
 * roughly three times the clinical threshold before anything is refused.
 *
 * BMI and WHR are server-written and never reach this editor, so they need no
 * row here — posting them directly is a 400 anyway.
 */
const PLAUSIBLE_RANGE = {
  weight: { min: 0.2, max: 500 },
  height: { min: 20, max: 300 },
  waistCircumference: { min: 20, max: 300 },
  hipCircumference: { min: 20, max: 300 },
};

const toKg = (value, unit) => (unit === "lb" ? value * LB_TO_KG : value);
const toCmFromLength = (value, unit) => (unit === "in" ? value * IN_TO_CM : value);
const ftInToCm = (feet, inches) => ((feet || 0) * 12 + (inches || 0)) * IN_TO_CM;

/**
 * The range as the operator sees it. Stating "0.5–500 kg" to somebody typing
 * pounds is a message they have to do arithmetic on before they can act, so the
 * bounds are converted into whatever unit is selected — including ft/in, where
 * the two-field layout means centimetres would be the wrong thing to quote.
 */
const describeRange = (paramKey, range, unit) => {
  /* Round the floor UP and the ceiling DOWN. Rounding both to nearest would
     quote a range slightly WIDER than the one enforced — 0.2 kg is 0.44 lb, and
     "0.4 lb" would name a value the check then rejects. A quoted bound must be
     one the operator can actually type. */
  const lo = (n) => Math.ceil(n * 10) / 10;
  const hi = (n) => Math.floor(n * 10) / 10;

  /* Keyed on the UNIT, not the parameter. Waist and hip are cm|in — a pair a
     parameter-keyed version had no branch for, so they would have fallen
     through and quoted centimetres at somebody typing inches. */
  if (unit === "lb") return `${lo(range.min * KG_TO_LB)} and ${hi(range.max * KG_TO_LB)} lb`;
  if (unit === "in") return `${lo(range.min * CM_TO_IN)} and ${hi(range.max * CM_TO_IN)} in`;
  if (unit === "ft/in") {
    const ftIn = (cm) => {
      const total = cm / IN_TO_CM;
      const ft = Math.floor(total / 12);
      const inch = Math.round(total - ft * 12);
      return ft ? `${ft}'${inch}"` : `${inch}"`;
    };
    return `${ftIn(range.min)} and ${ftIn(range.max)}`;
  }
  return `${range.min} and ${range.max} ${paramKey === "weight" ? "kg" : "cm"}`;
};

/**
 * "" when the value is fine, otherwise the message to show under the field.
 *
 * EXPORTED and used verbatim by the component below — a test that re-implemented
 * this arithmetic would only prove the test right.
 *
 * `numericValue` is the CANONICAL METRIC value, never what was typed: 1100 lb is
 * 498.9 kg and inside the range, and comparing the typed 1100 against a kilogram
 * bound would reject a legitimate reading.
 */
export const rangeErrorFor = (paramKey, label, numericValue, unit) => {
  const range = PLAUSIBLE_RANGE[paramKey];
  if (!range || numericValue == null || !isFinite(numericValue)) return "";
  if (numericValue >= range.min && numericValue <= range.max) return "";

  return `Enter a ${String(label || paramKey).toLowerCase()} between ${describeRange(
    paramKey,
    range,
    unit
  )}.`;
};

// Round to 2-decimal precision — used across every float input/display so
// stray floating-point noise (72.55000001) doesn't leak into the UI or the
// save payload.
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * FIVE DIGITS. `type="number"` ignores maxLength — that is exactly why a
 * twenty-digit weight went in — and `min`/`max` only gate form validation, not
 * typing, so the only place this can be enforced is the change handler.
 *
 * Nothing downstream will catch it either: the service validates that a value is
 * PRESENT and that the parameter is not an auto one, and nothing else
 * (api-anthropometric-v2-service/validations/anthropometric.go:13). BMI and WHR
 * are then computed server-side from whatever was stored, so one absurd weight
 * takes the derived parameters with it.
 *
 * Five is comfortable for every measure this editor takes, in either unit:
 * 250 cm, 199.99 kg, 440.92 lb, 96 in. It is the user's chosen bound.
 */
const MAX_DIGITS = 5;

// Counting DIGITS rather than characters, so "999.99" is five and not six.
//
// STOPS DEAD once the budget is spent, rather than skipping digits and keeping
// the punctuation after them: dropping only the digits turned a converted
// 220462.44 into "22046." — a trailing dot with nothing behind it. Ending the
// string there also means a 5-digit integer cannot grow a decimal point, which
// is right, because anything after it would be a sixth digit.
const capDigits = (s, max) => {
  let seen = 0;
  let out = "";
  for (const ch of s) {
    const isDigit = ch >= "0" && ch <= "9";
    if (isDigit && seen === max) break;
    if (isDigit) seen += 1;
    out += ch;
  }
  return out;
};

// EXPORTED for its own tests — every numeric write in this editor goes
// through it, so its edge cases are the guard.
// Clamp a user-typed string to 2 decimal places without stripping trailing
// zeros while the user is still typing, then to MAX_DIGITS. Called on every
// change so an accidental third decimal is discarded before it hits state.
export const clampValue = (raw) => {
  if (raw == null) return "";

  /* STRIP FIRST, and this is the half a digit cap alone does not do.
     `type="number"` accepts far more than digits:
       · EXPONENT NOTATION. "1e308" is four digits and sails through any length
         cap, and parseFloat makes it 1e308 — which is finite and positive, so
         the Save gate passes it. A cap that only counts digits does nothing
         here at all.
       · A MINUS SIGN. The ft/in branch has no positivity guard, so feet "-5"
         became -152.4 cm with Save enabled.
     Keeping only digits and a single decimal point closes both, and matches how
     the rest of this codebase caps a numeric field — DrugScheduleEditor:369 and
     the shared Stepper in controls.js:199 both strip before they slice. */
  const kept = String(raw).replace(/[^0-9.]/g, "");
  const first = kept.indexOf(".");
  const s =
    first === -1
      ? kept
      : kept.slice(0, first + 1) + kept.slice(first + 1).replace(/\./g, "");

  const dot = s.indexOf(".");
  const dp2 = dot === -1 ? s : s.slice(0, dot + 3); // "72.555" → "72.55"
  const capped = capDigits(dp2, MAX_DIGITS);

  /* A trailing "." is a legitimate half-typed state ("72." on the way to
     "72.5") — but NOT once the digit budget is spent, because nothing can
     follow it. Leaving it there gives a dead decimal point the user can type
     at forever with no effect. */
  const digits = capped.replace(/\D/g, "").length;
  return capped.endsWith(".") && digits === MAX_DIGITS ? capped.slice(0, -1) : capped;
};

// Accordion chevron — the board's icon/chev-up-16 (points up while open).
const Chevron = ({ open }) => (
  <span className={`an-card-chevron${open ? " is-open" : ""}`} aria-hidden="true">
    <ChevronUpIcon />
  </span>
);
Chevron.propTypes = { open: PropTypes.bool };

// Segmented pill switch for the unit toggle — matches the kg | lb / cm | in
// affordance in the mock. Two-option only; single-value semantics.
const UnitPill = ({ options, value, onChange, disabled }) => (
  <div className="an-unit-pill" role="group" aria-label="Unit">
    {options.map((u) => (
      <button
        key={u}
        type="button"
        className={`an-unit-pill-btn${u === value ? " is-on" : ""}`}
        onClick={() => onChange(u)}
        aria-pressed={u === value}
        disabled={disabled}
      >
        {u}
      </button>
    ))}
  </div>
);
UnitPill.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Look up the ParameterEntry for a given parameter key in the grouped doc.
const findParameter = (parameters, key) => {
  for (const p of parameters || []) {
    if (p.parameter === key) return p;
  }
  return null;
};

// ── ParamEditor — one card body, per parameter kind ────────────────────
// Exported so the AI-transcript flow can reuse this exact editor body
// inside its own outer chrome (theme + eyebrow + failure banner).
// `bypassSameAsLatest` disables the "no changes to save" gate — used by
// transcript-mode wrappers where the reviewer's Save is meant to commit
// the AI-seeded values verbatim.
export const ParamEditor = ({ meta, latestOfThis, onSave, onCancel, submitting, bypassSameAsLatest, menuClassName, saveLabel, savingLabel, cancelLabel, draftKey }) => {
  const isHeight = meta.key === "height";
  const isWeight = meta.key === "weight";
  const isWaist = meta.key === "waistCircumference";
  const isHip = meta.key === "hipCircumference";
  const isBlood = meta.key === "bloodGroup";

  const initialUnit = latestOfThis && latestOfThis.unit ? latestOfThis.unit : (meta.units[0] || "");
  const initialNumeric = latestOfThis && latestOfThis.numericValue != null ? latestOfThis.numericValue : "";

  // ZY-10356 Phase 1 — transcript-mode draft preservation. Standalone
  // callers leave draftKey undefined → no-op hook. Transcript's
  // AnthropometricForm passes formId as draftKey.
  const [draft, saveDraft] = useCardDraft(draftKey);

  const [unit, setUnit] = useState(() => (
    draft && "unit" in draft ? draft.unit : initialUnit
  ));
  // For height ft/in we split into feet + inches; otherwise a single value.
  const [valueNum, setValueNum] = useState(() => (
    draft && "valueNum" in draft ? draft.valueNum : (initialNumeric === "" ? "" : String(initialNumeric))
  ));
  const [feet, setFeet] = useState(() => (draft && "feet" in draft ? draft.feet : ""));
  const [inches, setInches] = useState(() => (draft && "inches" in draft ? draft.inches : ""));
  // Tracks whether the user has edited the feet/inches inputs since the last
  // unit toggle. If they haven't (i.e. ft/in was purely derived from the
  // canonical cm), we skip the ft/in → cm recompute on toggle-back so the
  // integer-inch round-off doesn't leak into the stored cm value.
  const [ftInDirty, setFtInDirty] = useState(() => (
    draft && "ftInDirty" in draft ? draft.ftInDirty : false
  ));
  // Same cap as the Value box — these are number inputs too, so nothing else
  // bounds what can be typed into them.
  const editFeet = (v) => { setFeet(clampValue(v)); setFtInDirty(true); };
  const editInches = (v) => { setInches(clampValue(v)); setFtInDirty(true); };
  // Blood-group prefill: empty valueDisplay on the server means the previous
  // save was the "None" (unset) pick — preselect that in the dropdown so the
  // user sees their prior choice reflected.
  const [bloodGroup, setBloodGroup] = useState(() => {
    if (draft && "bloodGroup" in draft) return draft.bloodGroup;
    if (!latestOfThis) return BLOOD_GROUPS[0] && BLOOD_GROUPS[0].key;
    if (!latestOfThis.valueDisplay) return BLOOD_GROUP_NONE;
    // Collapse a free-text transcript emission ("B positive",
    // "O-neg") to the canonical dropdown key ("B+", "O-"). Without
    // this the Select's `value` doesn't match any option and the
    // dropdown shows blank; a subsequent Save would then write the
    // raw spoken string into the DB. Fall back to the raw value so
    // a legitimate exact match (e.g. already-canonical "B+") still
    // selects.
    return normaliseBloodGroup(latestOfThis.valueDisplay) || latestOfThis.valueDisplay;
  });
  const [testDate, setTestDate] = useState(() => (
    draft && "testDate" in draft
      ? draft.testDate
      : (latestOfThis && latestOfThis.testDate
        ? new Date(latestOfThis.testDate).toISOString().slice(0, 10)
        : todayISO())
  ));

  // ZY-10356 Phase 1 — sync persistent fields to the modal-root draft
  // store on every change. No-op outside transcript mode.
  useEffect(() => {
    saveDraft({
      unit, valueNum, feet, inches, ftInDirty, bloodGroup, testDate,
    });
  }, [saveDraft, unit, valueNum, feet, inches, ftInDirty, bloodGroup, testDate]);

  /**
   * Pre-fill feet/inches from the stored cm — for the card that OPENS already in
   * ft/in, and for the case where the document arrives after mount. A cm -> ft/in
   * toggle is handled by changeUnit below, from the live value rather than the
   * stored one.
   *
   * `feet` and `inches` are deliberately NOT dependencies. With them in the list
   * this re-fired the moment BOTH boxes went empty, so clearing the pair to
   * retype snapped the stored value straight back in — the field fought anyone
   * trying to correct it, which is exactly when they most needed to. Without
   * them it runs only on a real unit change or a fresh document, and the
   * emptiness guard still reads the current values, because that render is the
   * one the effect runs in: changeUnit sets the pair and the unit together, so
   * by the time this sees unit === "ft/in" the boxes are already filled and it
   * correctly stands down.
   */
  useEffect(() => {
    if (isHeight && unit === "ft/in" && initialNumeric && !feet && !inches) {
      // Convert stored cm to ft + in for pre-fill.
      const totalIn = initialNumeric / IN_TO_CM;
      const f = Math.floor(totalIn / 12);
      const i = Math.round(totalIn - f * 12);
      setFeet(String(f));
      setInches(String(i));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, isHeight, initialNumeric]);

  // Auto-convert the current in-progress value when the user toggles units,
  // instead of clearing the input. Preserves the user's intent — switching
  // "72.5 kg" → "lb" now shows "159.79 lb", not a blank field.
  const changeUnit = (nextUnit) => {
    if (nextUnit === unit) return;
    if (isHeight) {
      if (nextUnit === "ft/in") {
        // cm → ft + in (integer-inch approximation for the display; the
        // canonical cm in valueNum stays intact so a toggle-back is lossless
        // unless the user edits feet/inches).
        const cm = parseFloat(valueNum);
        if (isFinite(cm) && cm > 0) {
          const totalIn = cm / IN_TO_CM;
          const f = Math.floor(totalIn / 12);
          const i = Math.round(totalIn - f * 12);
          setFeet(clampValue(String(f)));
          setInches(clampValue(String(i)));
        }
        setFtInDirty(false);
      } else {
        // ft + in → cm. Only recompute cm if the user actually touched the
        // ft/in fields — otherwise preserve the exact canonical cm.
        if (ftInDirty) {
          const f = parseFloat(feet) || 0;
          const i = parseFloat(inches) || 0;
          if (f > 0 || i > 0) {
            const cm = round2(ftInToCm(f, i));
            setValueNum(clampValue(String(cm)));
          }
        }
        setFeet("");
        setInches("");
        setFtInDirty(false);
      }
    } else if (isWeight) {
      const n = parseFloat(valueNum);
      if (isFinite(n) && n > 0) {
        const converted = nextUnit === "lb" ? n * KG_TO_LB : n * LB_TO_KG;
        setValueNum(clampValue(String(round2(converted))));
      }
    } else if (isWaist || isHip) {
      const n = parseFloat(valueNum);
      if (isFinite(n) && n > 0) {
        const converted = nextUnit === "in" ? n * CM_TO_IN : n * IN_TO_CM;
        setValueNum(clampValue(String(round2(converted))));
      }
    }
    setUnit(nextUnit);
  };

  // Compute the metric numeric value + display string for the current input.
  const prepared = useMemo(() => {
    if (isBlood) {
      // "None" is the explicit unset pick — serialise as an empty
      // valueDisplay so the content table renders "—". Backend allows this
      // only for the bloodGroup parameter.
      //
      // Unit is emitted as "None" (matches the LI unit dropdown's "None"
      // option) so blood group aligns with the way clinicians see the
      // rest of the Anthro-mapped SLTs — a value with a unit, even when
      // that unit is intentionally blank.
      const isNone = bloodGroup === BLOOD_GROUP_NONE;
      return {
        numericValue: null,
        valueDisplay: isNone ? "" : bloodGroup,
        unit: "None",
      };
    }
    if (isHeight && unit === "ft/in") {
      const f = parseFloat(feet) || 0;
      const i = parseFloat(inches) || 0;
      if (f === 0 && i === 0) return null;
      // When ft/in was purely derived from the canonical cm (not user-edited),
      // save the canonical cm instead of the round-tripped ft/in so a toggle
      // roundtrip never loses precision.
      const canonicalCm = parseFloat(valueNum);
      const numericValue =
        !ftInDirty && isFinite(canonicalCm) && canonicalCm > 0
          ? round2(canonicalCm)
          : round2(ftInToCm(f, i));
      return {
        numericValue,
        valueDisplay: `${f}'${i}"`,
        unit,
      };
    }
    const n = parseFloat(valueNum);
    if (!isFinite(n) || n <= 0) return null;
    let metric = n;
    if (isWeight) metric = toKg(n, unit);
    else if (isHeight) metric = toCmFromLength(n, unit);
    else if (isWaist || isHip) metric = toCmFromLength(n, unit);
    return {
      numericValue: Number(metric.toFixed(2)),
      valueDisplay: `${n} ${unit}`.trim(),
      unit,
    };
  }, [isBlood, isHeight, isWeight, isWaist, isHip, unit, feet, inches, valueNum, bloodGroup, ftInDirty]);

  /**
   * Out of plausible range. Checked on prepared.numericValue — the CANONICAL
   * metric value — so 154 lb and 70 kg are judged identically rather than the
   * pounds figure being measured against a kilogram bound.
   *
   * This gates SAVE rather than truncating what is typed: a range cannot be
   * enforced per keystroke, because "1" is on the way to "150". The digit cap
   * handles the keystrokes; this handles the answer.
   */
  const rangeError = useMemo(
    () =>
      rangeErrorFor(meta.key, meta.label, prepared && prepared.numericValue, unit),
    [meta.key, meta.label, prepared, unit]
  );

  // Mirrors backend transformer.IsSameCurrent — disables Save when the form
  // matches the parameter's latest Current on every UI-visible field. For
  // Blood group compare valueDisplay; for measure params compare numericValue
  // (2-decimal precision), unit, and testDate.
  const sameAsLatest = useMemo(() => {
    if (!latestOfThis || !prepared) return false;
    const existingDate = latestOfThis.testDate
      ? new Date(latestOfThis.testDate).toISOString().slice(0, 10)
      : "";
    if (existingDate !== testDate) return false;
    if (isBlood) {
      return (latestOfThis.valueDisplay || "") === (prepared.valueDisplay || "");
    }
    if ((latestOfThis.unit || "") !== (prepared.unit || "")) return false;
    const r2 = (n) => Math.round(n * 100) / 100;
    const existingNum = latestOfThis.numericValue == null ? null : r2(latestOfThis.numericValue);
    const nextNum = prepared.numericValue == null ? null : r2(prepared.numericValue);
    return existingNum === nextNum;
  }, [latestOfThis, prepared, testDate, isBlood]);

  // Standalone gates on `!sameAsLatest` so an accidental Save without
  // changes is a no-op. Transcript-mode reviewers pass
  // `bypassSameAsLatest` so their explicit Save always commits the
  // AI-seeded values.
  const canSubmit =
    !submitting &&
    prepared !== null &&
    !rangeError &&
    !!testDate &&
    (bypassSameAsLatest || !sameAsLatest);

  const submit = () => {
    if (!canSubmit) return;
    onSave({
      parameter: meta.key,
      numericValue: prepared.numericValue,
      unit: prepared.unit,
      valueDisplay: prepared.valueDisplay,
      testDate: toIsoUtc(testDate),
    });
  };

  return (
    <div className="an-card-body">
      {/* Value + unit row */}
      {isBlood ? (
        <div className="sec-field">
          <label className="sec-label">
            Value <span className="sec-label-required">*</span>
          </label>
          <Select
            value={bloodGroup}
            options={BLOOD_GROUPS}
            getKey={(o) => o.key}
            getLabel={(o) => o.label}
            onChange={setBloodGroup}
            placeholder="Select group"
            /* Inside the transcript modal (z-index 1500) the caller
               passes `menuClassName="ait-menu"` so the portaled menu
               (default z-index 200) is raised above the modal via the
               scoped `body >` selector in aiTranscript.scss. Standalone
               editor leaves this empty and Select ignores empty strings.
               Without it, clicks on Blood-group options landed on the
               modal backdrop and the dropdown appeared to "not work". */
            menuClassName={menuClassName || ""}
          />
        </div>
      ) : isHeight && unit === "ft/in" ? (
        <div className="an-row2 is-ftin">
          <div className="an-col-value">
            <label className="sec-label">
              Feet <span className="sec-label-required">*</span>
            </label>
            <input
              className="sec-input"
              type="number"
              min="0"
              step="1"
              value={feet}
              onChange={(e) => editFeet(e.target.value)}
              placeholder="0"
              disabled={submitting}
            />
          </div>
          <div className="an-col-value">
            <label className="sec-label">
              Inches <span className="sec-label-required">*</span>
            </label>
            <input
              className="sec-input"
              type="number"
              min="0"
              max="11"
              step="1"
              value={inches}
              onChange={(e) => editInches(e.target.value)}
              placeholder="0"
              disabled={submitting}
            />
          </div>
          <div className="an-col-unit">
            <label className="sec-label">
              Unit <span className="sec-label-required">*</span>
            </label>
            <UnitPill options={meta.units} value={unit} onChange={changeUnit} disabled={submitting} />
          </div>
        </div>
      ) : (
        <div className="an-row2">
          <div className="an-col-value">
            <label className="sec-label">
              Value <span className="sec-label-required">*</span>
            </label>
            <input
              className="sec-input"
              type="number"
              step="0.01"
              value={valueNum}
              onChange={(e) => setValueNum(clampValue(e.target.value))}
              placeholder="0.0"
              disabled={submitting}
            />
          </div>
          <div className="an-col-unit">
            <label className="sec-label">
              Unit <span className="sec-label-required">*</span>
            </label>
            <UnitPill options={meta.units} value={unit} onChange={changeUnit} disabled={submitting} />
          </div>
        </div>
      )}

      {/* Outside the ternary so it covers all three value shapes — the single
          box, and the ft/in pair, either of which can land out of range. */}
      {rangeError && <p className="fam-field-error">{rangeError}</p>}

      <div className="sec-field">
        <label className="sec-label" htmlFor={`an-date-${meta.key}`}>
          Date recorded <span className="sec-label-required">*</span>
        </label>
        <div className="an3-date-wrap">
          <input
            id={`an-date-${meta.key}`}
            className="sec-input sec-input-date"
            type="date"
            value={testDate}
            onChange={(e) => setTestDate(e.target.value)}
            max={todayISO()}
            disabled={submitting}
          />
          <span className="an3-date-icon" aria-hidden="true"><CalendarIcon /></span>
        </div>
      </div>

      <div className="sec-editor-actions an-editor-actions">
        <button
          type="button"
          className="sec-btn sec-btn-ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          {cancelLabel || "Cancel"}
        </button>
        <button
          type="button"
          className="sec-btn sec-btn-primary"
          onClick={submit}
          disabled={!canSubmit}
        >
          {submitting ? (savingLabel || "Saving…") : (saveLabel || "Save measurement")}
        </button>
      </div>
    </div>
  );
};
ParamEditor.propTypes = {
  meta: PropTypes.object.isRequired,
  latestOfThis: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  /** Transcript-mode escape hatch — skips the "no changes to save" gate. */
  bypassSameAsLatest: PropTypes.bool,
  /** Extra class the portaled Select menu should receive. Transcript
      callers pass "ait-menu" so the menu stacks above the modal. */
  menuClassName: PropTypes.string,
  /** Overrides for the primary CTA label. Transcript callers pass
      "Add"/"Adding…" so the reviewer's Save reads as an Add on the
      AI-populated card; standalone defaults to "Save measurement". */
  saveLabel: PropTypes.string,
  savingLabel: PropTypes.string,
  /** Overrides the ghost button label. Transcript callers pass "Reject". */
  cancelLabel: PropTypes.string,
};

ParamEditor.defaultProps = {
  bypassSameAsLatest: false,
  menuClassName: "",
};

// ── AnthropometricEditor — rail wrapper ────────────────────────────────
const AnthropometricEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [doc, setDoc] = useState(null);
  // Multi-card rail (Addictions pattern): each parameter click opens
  // its own card and stacks it on top. `openKeys[0]` is the topmost
  // card. `collapsedKeys` tracks which cards have been folded via the
  // chevron — a card can be OPEN (mounted) and collapsed at the same
  // time; closing (Cancel / Save success) removes it from `openKeys`
  // entirely. Rail is `editorOnDemand`, so the shell hides its column
  // whenever `openKeys.length === 0`.
  const [openKeys, setOpenKeys] = useState([]);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());

  // Publish this editor's open-card count into the shell registry so the
  // sidebar badge + leave-page warning see Anthropometric alongside the
  // ten useSheetStack-based sections (ZY-10350). This editor doesn't
  // share the standard sheet-stack shape — each entry in `openKeys` is a
  // parameter card the reviewer has clicked open — but its "N cards
  // open" is just `openKeys.length`.
  useSheetCountRegistration("anthropometric", openKeys.length);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(() => {
    if (!patientId) return;
    const svc = new AnthropometricService();
    svc.list(
      patientId,
      (res) => setDoc(res || { parameters: [], version: 0 }),
      () => setDoc({ parameters: [], version: 0 })
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(ANTH_V2_REFRESH, load);
    EventService.addListener(LI_V2_REFRESH, load);
    return () => {
      EventService.removeListener(ANTH_V2_REFRESH, load);
      EventService.removeListener(LI_V2_REFRESH, load);
    };
  }, [load]);

  // ZY-10356 — same ECHO-modal gate as useSheetStack. ANTH_V2_SELECT_PARAM
  // fires from the embedded Anthropometric list inside the transcript
  // modal too; without the gate the shell rail opens a card behind the
  // modal that the reviewer then finds on their next standalone visit.
  // Shared helper so this editor and useSheetStack can't drift on the
  // gate rule.
  const { gatedRef: transcriptModalGateRef } = useTranscriptModalGate();
  useEffect(() => {
    const onSelect = (key) => {
      if (transcriptModalGateRef.current) return;
      if (!EDITOR_KEYS.includes(key)) return;
      // Already-open card — re-clicking surfaces it (uncollapses it if
      // it was folded) without stacking a duplicate.
      setOpenKeys((prev) => (prev.includes(key) ? prev : [key, ...prev]));
      setCollapsedKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };
    EventService.addListener(ANTH_V2_SELECT_PARAM, onSelect);
    return () => EventService.removeListener(ANTH_V2_SELECT_PARAM, onSelect);
    // Ref identity is stable across renders; include it to satisfy the
    // exhaustive-deps lint without resubscribing.
  }, [transcriptModalGateRef]);

  // Shell contract for `editorOnDemand: true` — RAIL_OPEN whenever a
  // card is showing, RAIL_CLOSE when the stack empties. Emitted on
  // unmount too so a page navigation frees the rail column.
  const isOpen = openKeys.length > 0;
  useEffect(() => {
    EventService.emit(isOpen ? RAIL_OPEN : RAIL_CLOSE);
  }, [isOpen]);
  useEffect(() => () => EventService.emit(RAIL_CLOSE), []);

  const parameters = (doc && doc.parameters) || [];

  const closeCard = (key) => {
    setOpenKeys((prev) => prev.filter((k) => k !== key));
    setCollapsedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const toggleCollapsed = (key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = (body) => {
    setSubmitting(true);
    setFeedback(null);
    const svc = new AnthropometricService();
    svc.save(
      patientId,
      { ...body, expectedVersion: (doc && doc.version) || 0 },
      (res) => {
        setSubmitting(false);
        setDoc(res || doc);
        setFeedback(null);
        EventService.emit(ANTH_V2_REFRESH);
        // Anthro Save writes into api-lab-investigation-v2 (ADR-0001
        // Phase 4). Fire LI's refresh event too so anything watching
        // the LI section — the Labs card on the dashboard, the LI
        // section list, the TrendModal — picks up the write without
        // waiting for the next mount.
        EventService.emit(LI_V2_REFRESH);
        // Auto-close only THIS card. Other stacked cards stay open so
        // the reviewer can keep editing the rest. The evidence of a
        // successful save is the refreshed Content-table row — no
        // confirmation strip needed. `case === "unchanged"` no-ops
        // ride the same path.
        closeCard(body.parameter);
      },
      (err) => {
        setSubmitting(false);
        setFeedback({
          kind: "err",
          text:
            (err && err.message) ||
            "Save failed — please retry (patient profile may be unreachable)",
        });
      }
    );
  };

  if (!patientId) {
    return (
      <div className="ph-slot-empty is-editor">
        <p>Waiting for patient…</p>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="an-acc">
      {openKeys.map((key) => {
        const meta = EDITOR_META.find((m) => m.key === key);
        if (!meta) return null;
        const collapsed = collapsedKeys.has(key);
        const entry = findParameter(parameters, meta.key);
        const latestCurrent = entry ? entry.current : null;

        return (
          /* Keyed on whether the document has ARRIVED, not just on the
             parameter. ParamEditor seeds valueNum/feet/inches with
             useState, which only reads its initial value once — so a card
             that mounts BEFORE the fetch resolves would render blank even
             for a patient who has a stored value. Flipping the key when
             doc goes null → loaded forces the remount that reads the
             arrived data; ANTH_V2_REFRESH after a save keeps the same
             "ready" key so it does not throw away an edit in progress. */
          <div
            className={`an-card${collapsed ? "" : " is-open"}`}
            key={`${meta.key}-${doc ? "ready" : "pending"}`}
          >
            <button
              type="button"
              className="an-card-head"
              aria-expanded={!collapsed}
              onClick={() => toggleCollapsed(meta.key)}
            >
              <span className="an-card-title">{meta.label}</span>
              <Chevron open={!collapsed} />
            </button>
            {!collapsed && (
              <ParamEditor
                meta={meta}
                latestOfThis={latestCurrent}
                onSave={handleSave}
                onCancel={() => closeCard(meta.key)}
                submitting={submitting}
              />
            )}
          </div>
        );
      })}
      {feedback && (
        <div className={`cc-feedback is-${feedback.kind} an-feedback`}>{feedback.text}</div>
      )}
    </div>
  );
};

AnthropometricEditor.propTypes = {
  patient: PropTypes.object,
};

export default AnthropometricEditor;
